import { attachCaptchaToken } from '@/utils/captcha';

import { CaptchaFormBindingProps } from './CaptchaForm';

type UseFormikCaptchaFormParams<T extends object> = {
  values: T;
  validateForm: () => Promise<unknown>;
  setTouched: (fields: object, shouldValidate?: boolean) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  onSubmit?: (values: T & { captchaToken: string }) => Promise<unknown> | unknown;
  mapValues?: (values: T & { captchaToken: string }) => object;
  canSubmit?: () => boolean;
  touchAllOnError?: object;
};

export function useFormikCaptchaForm<T extends object>({
  values,
  validateForm,
  setTouched,
  setSubmitting,
  onSubmit,
  mapValues,
  canSubmit,
  touchAllOnError,
}: UseFormikCaptchaFormParams<T>): CaptchaFormBindingProps<T> {
  const onCaptchaSubmit = async (
    captchaValues: T & { captchaToken: string }
  ) => {
    if (canSubmit && !canSubmit()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = mapValues
        ? mapValues(captchaValues)
        : captchaValues;

      await onSubmit?.(
        attachCaptchaToken(payload as T, captchaValues.captchaToken)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validateCaptchaForm = async () => {
    if (canSubmit && !canSubmit()) {
      return false;
    }

    const errors = await validateForm();
    const isValid = Object.keys(errors as object).length === 0;

    if (!isValid && touchAllOnError) {
      setTouched(touchAllOnError, true);
    }

    return isValid;
  };

  return {
    onCaptchaSubmit,
    validateForm: validateCaptchaForm,
    getValues: () => values,
  };
}
